import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/core/auth/authStore';
import type { AdminRole, CrudAction } from '@/core/auth/types';
import { type FeatureFlag, isEnabled } from '@/core/config/featureFlags';
import { hasPermission, isWebAllowedRole } from './permissions';

interface RouteGuardProps {
  children: ReactNode;
  roles?: AdminRole[];
  module?: string;
  action?: CrudAction;
  /** Reserved-feature gate: if set and the flag is off, the route is blocked. */
  flag?: FeatureFlag;
}

export function RouteGuard({ children, roles, module, action, flag }: RouteGuardProps) {
  const status = useAuthStore((s) => s.status);
  const role = useAuthStore((s) => s.role);
  const permissions = useAuthStore((s) => s.permissions);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return <div className="grid h-full place-items-center text-fv-muted">Loading…</div>;
  }
  if (status === 'sessionExpired') {
    return <Navigate to="/session-expired" replace state={{ from: location.pathname }} />;
  }
  if (status !== 'authenticated' || !role) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Temp-password login: force a password change before anything else.
  if (mustChangePassword && location.pathname !== '/force-change-password') {
    return <Navigate to="/force-change-password" replace />;
  }
  if (!isWebAllowedRole(role)) {
    return <Navigate to="/access-denied" replace />;
  }
  if (roles && !roles.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (flag && !isEnabled(flag)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (module && action && !hasPermission(permissions, module, action)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
